import { LuPhoneCall, LuClock3, LuEye } from "react-icons/lu";
import { MdOutlineEmail, MdOutlineLocationOn } from "react-icons/md";
import { BiClinic } from "react-icons/bi";
import { useDoctorQuery } from "../../hooks/useDoctorQuery";
import { FaRegHospital } from "react-icons/fa6";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useDoctorContext } from "../../context/DoctorContext";
import NeedAuthModal from "../Modals/NeedAuthModal";
import GuestBookingModal from "../Modals/GuestInformationModal";
import { useState } from "react";
import AnimatedButton from "../Buttons/AnimatedButton";
import { useLanguage } from "../../context/LanguageContext";

const formatTime = (timeStr, language) => {
    if (!timeStr || typeof timeStr !== "string") return "";
    const [hour, minute] = timeStr.split(":");
    const hourNum = parseInt(hour, 10);
    if (isNaN(hourNum)) return timeStr;
    const ampm = hourNum >= 12 ? (language === 'ar' ? 'م' : 'PM') : (language === 'ar' ? 'ص' : 'AM');
    const formattedHour = hourNum % 12 || 12;
    return `${formattedHour}:${minute} ${ampm}`;
};

const ClinicsSection = () => {
    const { data: doctor } = useDoctorQuery();
    const { slug } = useParams();
    const { t, language } = useLanguage();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isGuestModalVisible, setIsGuestModalVisible] = useState(false);
    const [pendingRoute, setPendingRoute] = useState(null);
    const [expandedClinic, setExpandedClinic] = useState(null);

    const isAuthorized =
        !!localStorage.getItem(`userTkn-${slug}`) ||
        !!sessionStorage.getItem(`userTkn-${slug}`);
    const navigate = useNavigate();
    const { setHasClinics } = useDoctorContext();
    const hasClinics = doctor?.clinics?.length > 0;

    const url = window.location.href;
    const currentPath = url.substring(url.lastIndexOf('/') + 1);
    
    // Check if current path is "services" to show all clinics
    const isServicesPage = currentPath === "clinics";
    
    if (!hasClinics) {
        setHasClinics(false)
        return null
    } else {
        setHasClinics(true)
    }

    const handleProtectedNavigation = (route) => {
        if (!isAuthorized) {
            setPendingRoute(route);
            setIsModalVisible(true);
        } else {
            navigate(route);
        }
    };

    const handleGuestBooking = () => {
        setIsModalVisible(false);
        setIsGuestModalVisible(true);
    };

    const handleGuestInfoSubmit = (guestInfo) => {
        setIsGuestModalVisible(false);
        sessionStorage.setItem(`guestInfo-${slug}`, JSON.stringify(guestInfo));
        sessionStorage.setItem(`isGuestBooking-${slug}`, 'true');
        navigate(pendingRoute || `/${slug}/book-appointment`);
    };

    const toggleClinicDetails = (clinicId) => {
        setExpandedClinic(expandedClinic === clinicId ? null : clinicId);
    };

    // Get clinics to display - show all if on services page, otherwise limit to 4
    const clinicsToShow = isServicesPage 
        ? doctor?.clinics || []
        : doctor?.clinics?.slice(0, 4) || [];
    
    const hasMoreClinics = !isServicesPage && doctor?.clinics?.length > 4;

    const ClinicDetailsModal = ({ clinic, isOpen, onClose }) => {
        if (!isOpen) return null;
console.log(clinic.workingDays);

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <motion.div
                    className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                >
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-2xl font-bold text-primary flex items-center gap-2">
                            <BiClinic className="text-2xl" /> {clinic.name}
                        </h3>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 text-2xl"
                        >
                            ×
                        </button>
                    </div>

                    <div className="space-y-6 text-sm text-textPrimary">
                        {/* Address */}
                        <div className="flex items-start gap-3 bg-primary/10 p-4 rounded-xl">
                            <MdOutlineLocationOn className="text-2xl mt-0.5 text-primary" />
                            <div>
                                <p className="font-semibold mb-2 text-lg">{t('clinics.address')}</p>
                                <p className="text-gray-700">{clinic.detailedAddress}</p>
                            </div>
                        </div>

                        {/* Contact Info */}
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="flex items-start gap-3 bg-primary/10 p-4 rounded-xl">
                                <LuPhoneCall className="text-2xl mt-0.5 text-primary" />
                                <div>
                                    <p className="font-semibold mb-2">{t('clinics.phone')}</p>
                                    <p className="text-gray-700">{clinic.contactPhone || t('clinics.notAvailable')}</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-3 bg-primary/10 p-4 rounded-xl">
                                <MdOutlineEmail className="text-2xl mt-0.5 text-primary" />
                                <div>
                                    <p className="font-semibold mb-2">{t('clinics.email')}</p>
                                    <p className="text-gray-700">{clinic.contactEmail || t('clinics.notAvailable')}</p>
                                </div>
                            </div>
                        </div>

                        {/* Working Hours */}
                        <div className="flex items-start gap-3">
                            <LuClock3 className="text-2xl mt-0.5 text-primary" />
                            <div className="w-full">
                                <p className="font-semibold mb-3 text-lg">{t('clinics.workingHours')}</p>
                                <div className="space-y-3 text-gray-700">
                                    {clinic.workingDays?.filter(day => day.isWorking)?.length > 0 ? (
                                        clinic.workingDays
                                            .filter(day => day.isWorking)
                                            .map(day => (
                                                
                                                <div key={day.id} className="border-l-4 border-primary pl-4 py-2">
                                                    <p className="font-medium text-primary text-base">{day.workingDayName}</p>
                                                    {day.availabilityHours?.length > 0 ? (
                                                        day.availabilityHours.map(hour => (
                                                            <div key={hour.id} className="ml-2 mt-1">
                                                                <p className="text-gray-700">
                                                                    {formatTime(hour.startTime, language)} - {formatTime(hour.endTime, language)}
                                                                </p>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <h2>{t('clinics.noAvailabilityTime')}</h2>
                                                    )}
                                                </div>
                                            ))
                                    ) : (
                                        <p className="text-gray-500">{t('clinics.notAvailable')}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 flex gap-3">
                        <button
                            className="flex-1 bg-primary text-white font-semibold py-3 px-6 rounded-lg hover:bg-primary/90 transition-colors shadow-md hover:shadow-lg"
                            onClick={() => {
                                onClose();
                                handleProtectedNavigation(`/${slug}/book-appointment?ClinicId=${clinic.id}`);
                            }}
                        >
                            {t('clinics.bookAppointment')}
                        </button>
                        <button
                            onClick={onClose}
                            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            {t('common.close')}
                        </button>
                    </div>
                </motion.div>
            </div>
        );
    };

    return (
        <section className="sm:p-10 p-3 bg-[#F9FCFD]">
            <div className="text-center mb-14 ">
                <h2 className="text-4xl font-bold text-center text-textPrimary flex items-center justify-center gap-3 ">
                    <BiClinic className='text-3xl' /> 
                    {isServicesPage ? t('clinics.allAvailableClinics') : t('clinics.availableClinics')}
                </h2>
                <p className="mt-3 text-textPrimary/70 max-w-2xl mx-auto">
                    {isServicesPage 
                        ? t('clinics.allClinicsDesc')
                        : t('clinics.availableClinicsDesc')
                    }
                </p>
            </div>

            {hasClinics ? (
                <div>
                    <motion.div
                        className={`grid gap-6 md:grid-cols-2 ${
                            isServicesPage 
                                ? "lg:grid-cols-3 xl:grid-cols-3" 
                                : "lg:grid-cols-2 xl:px-48"
                        }`}
                        initial="hidden"
                        animate="visible"
                        variants={{
                            hidden: {},
                            visible: {
                                transition: {
                                    staggerChildren: 0.15
                                }
                            }
                        }}
                    >
                        {clinicsToShow.map((clinic) => (
                            <motion.div
                                key={clinic.id}
                                className="bg-[#EBF8FA] border border-primary/30 rounded-2xl p-6 shadow-md flex flex-col justify-between"
                                whileHover={{ scale: 1.03, boxShadow: "0 10px 20px rgba(0,0,0,0.12)" }}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                            >
                                <div>
                                    <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
                                        <BiClinic className="text-xl" /> {clinic.name}
                                    </h3>

                                    <div className="space-y-4 text-sm text-textPrimary">
                                        {/* Address */}
                                        <div className="flex items-start gap-3">
                                            <MdOutlineLocationOn className="text-lg mt-0.5 text-primary" />
                                            <div>
                                                <p className="font-semibold mb-1">{t('clinics.address')}</p>
                                                <p className="text-gray-700 line-clamp-2">{clinic.detailedAddress}</p>
                                            </div>
                                        </div>

                                        {/* Phone */}
                                        <div className="flex items-start gap-3">
                                            <LuPhoneCall className="text-lg mt-0.5 text-primary" />
                                            <div>
                                                <p className="font-semibold mb-1">{t('clinics.phone')}</p>
                                                <p className="text-gray-700">{clinic.contactPhone || t('clinics.notAvailable')}</p>
                                            </div>
                                        </div>

                                        {/* Email */}
                                        <div className="flex items-start gap-3">
                                            <MdOutlineEmail className="text-lg mt-0.5 text-primary" />
                                            <div>
                                                <p className="font-semibold mb-1">{t('clinics.email')}</p>
                                                <p className="text-gray-700">{clinic.contactEmail || t('clinics.notAvailable')}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6 flex gap-2">
                                     <AnimatedButton
                                            className="flex-1 bg-primary text-white font-semibold py-2 px-4 rounded-lg hover:bg-secondary/90 transition-colors shadow-md hover:shadow-lg"

                                            secondary
                                            onClick={() => handleProtectedNavigation(`/${slug}/book-appointment?ClinicId=${clinic.id}`)}
                                        >
                                              {t('clinics.bookHere')}
                                        </AnimatedButton>
                                    <button
                                        className="bg-white text-primary border border-primary font-semibold py-2 px-4 rounded-lg hover:bg-secondary hover:text-white transition-colors shadow-md flex items-center gap-2"
                                        onClick={() => toggleClinicDetails(clinic.id)}
                                    >
                                        <LuEye className="text-sm" />
                                        {t('clinics.details')}
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* See More Button - only show if not on services page and there are more clinics */}
                    {hasMoreClinics && (
                        <motion.div 
                            className="text-center mt-10"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.3 }}
                        >
                            <AnimatedButton
                                className="bg-primary text-primary border-2 border-primary font-semibold py-3 px-8 rounded-lg hover:bg-primary hover:text-white transition-colors shadow-md hover:shadow-lg flex items-center gap-2 mx-auto"
                                onClick={() => navigate(`/${slug}/clinics`)}
                            >
                                <BiClinic className="text-lg" />
                                {t('clinics.seeMoreClinics')}
                            </AnimatedButton>
                        </motion.div>
                    )}
                </div>
            ) : (
                <div className="text-center py-16 text-gray-600">
                    <HiOutlineBuildingOffice2 className="text-6xl mx-auto text-primary mb-4" />
                    <h3 className="text-xl font-semibold mb-2">{t('clinics.noClinicsAvailable')}</h3>
                    <p className="mb-4">{t('clinics.noClinicsDesc')}</p>
                    <button className="mt-2 bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors shadow-md hover:shadow-lg">
                        {t('clinics.contactUs')}
                    </button>
                </div>
            )}

            {/* Clinic Details Modal */}
            {expandedClinic && (
                <ClinicDetailsModal
                    clinic={doctor.clinics.find(c => c.id === expandedClinic)}
                    isOpen={!!expandedClinic}
                    onClose={() => setExpandedClinic(null)}
                />
            )}

            <NeedAuthModal
                open={isModalVisible}
                onConfirm={() => {
                    setIsModalVisible(false);
                    navigate(`/${slug}/auth`);
                }}
                onCancel={() => {
                    setIsModalVisible(false);
                    setPendingRoute(null);
                }}
                onGuestBooking={handleGuestBooking}
            />

            <GuestBookingModal
                open={isGuestModalVisible}
                onCancel={() => {
                    setIsGuestModalVisible(false);
                    setPendingRoute(null);
                }}
                onSubmit={handleGuestInfoSubmit}
            />
        </section>
    );
};

export default ClinicsSection;